import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { GradientModal } from '@/components/gradient-modal';
import { centered } from '@/components/use-shared-element';
import { EditorContext } from '@/features/editor/context/editor-context';
import { PendingChange, useExperienceApi } from '@/webapi/use-experience-api';
import { formatToLocalDate } from '@/features/details/utils';
import { VSpace } from '@/components/spacing';
import { Spinner } from '@/Spinner';

interface PendingChangesOverlayProps {
  isVisible: boolean;
  fromRef: React.MutableRefObject<HTMLElement>;
  onClose: () => void;
  onPublish: () => void;
  onIgnore: () => void;
}

/**
 * Renders an overlay displaying pending changes for an experience.
 *
 * @param isVisible - Whether the overlay is visible
 * @param fromRef - Reference to the element from which the overlay should animate
 * @param onClose - Callback when the overlay is closed
 * @param onPublish - Callback when the publish button is clicked
 * @param onIgnore - Callback when the ignore button is clicked
 */
export function PendingChangesOverlay({
  isVisible,
  fromRef,
  onClose,
  onPublish,
  onIgnore,
}: PendingChangesOverlayProps) {
  const {
    experienceState: { currentExperience },
  } = useContext(EditorContext);

  const { getPendingChangesSummary } = useExperienceApi();
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedChange, setExpandedChange] = useState<number | null>(null);

  useEffect(() => {
    if (isVisible && currentExperience?.id) {
      loadPendingChanges();
    }
  }, [isVisible, currentExperience?.id]);

  const loadPendingChanges = async () => {
    setIsLoading(true);
    try {
      const summary = await getPendingChangesSummary(currentExperience.id);
      setPendingChanges(summary);
    } catch (error) {
      console.error(`Failed to load pending changes`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = (ordinal: number) => {
    setExpandedChange(expandedChange === ordinal ? null : ordinal);
  };

  const updatedBy = currentExperience?.updatedBy;
  const updatedAt =
    currentExperience?.pendingUpdatingAt || currentExperience?.updatedAt;
  const formattedDate = updatedAt ? formatToLocalDate(updatedAt) : ``;

  const targetPosAndSize = centered(70, 50);

  const header = (
    <Header>
      <HeaderTitle>Unpublished Changes</HeaderTitle>
      {(updatedBy || formattedDate) && (
        <HeaderSubtitle>
          {updatedBy ? `Saved by ${updatedBy}` : `Changes saved`}
          {formattedDate ? ` · ${formattedDate}` : ``}
        </HeaderSubtitle>
      )}
    </Header>
  );

  const footer = (
    <Footer>
      <PublishButton onClick={onPublish}>Publish Now</PublishButton>
      <IgnoreButton onClick={onIgnore}>Dismiss</IgnoreButton>
    </Footer>
  );

  return (
    <GradientModal
      fromRef={fromRef}
      fromStyle={{
        borderRadius: `5rem`,
        backgroundColor: `#FFD2AC`,
        padding: `2rem 0 0 0`,
      }}
      toStyle={{
        borderRadius: `1.5rem`,
        backgroundColor: `#FFFFFF`,
        padding: `2rem 0 0 0`,
      }}
      closeTop="0.5rem"
      closeRight="2.5rem"
      isVisible={isVisible}
      showBackdrop
      targetPosAndSize={targetPosAndSize}
      onClose={onClose}
      header={header}
      footer={footer}
    >
      <Content>
        <VSpace value={2} />
        {isLoading && (
          <SpinnerWrapper>
            <Spinner relative size="40" color="#4a5568" />
            <LoadingText>Generating a changelog with AI...</LoadingText>
          </SpinnerWrapper>
        )}
        {!isLoading && !pendingChanges?.length && (
          <EmptyStateWrapper>
            <EmptyState>No specific changes recorded</EmptyState>
          </EmptyStateWrapper>
        )}
        {!isLoading && pendingChanges?.length > 0 && (
          <ChangesList>
            {pendingChanges.map((change) => (
              <ChangeItemWrapper key={change.ordinal}>
                <ChangeItem
                  onClick={() => toggleExpand(change.ordinal)}
                  isExpanded={expandedChange === change.ordinal}
                >
                  <ChangeOrdinal>{change.ordinal}</ChangeOrdinal>
                  <ChangeLabel>{change.name}</ChangeLabel>
                  <ExpandIconWrapper>
                    {expandedChange === change.ordinal ? (
                      <HiChevronUp size={20} />
                    ) : (
                      <HiChevronDown size={20} />
                    )}
                  </ExpandIconWrapper>
                </ChangeItem>
                {expandedChange === change.ordinal && (
                  <ChangeDescription>{change.description}</ChangeDescription>
                )}
              </ChangeItemWrapper>
            ))}
          </ChangesList>
        )}
        <VSpace value={1} />
      </Content>
    </GradientModal>
  );
}

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 2rem;
  text-align: center;
`;

const HeaderTitle = styled.h3`
  font-family: Inter, serif;
  font-size: 1.6rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  font-family: Inter, serif;
  font-size: 1.2rem;
  font-weight: 400;
  color: #718096;
  margin: 0.5rem 0 0;
`;

const Content = styled.div`
  position: relative;
  padding: 2rem 0;
  overflow-y: auto;
  min-height: 10rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-left: 10px;
`;

const SpinnerWrapper = styled.div`
  position: absolute;
  inset: 0;
  top: -10rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 4rem 0;
  gap: 1.5rem;
`;

const LoadingText = styled.p`
  font-family: Inter, serif;
  font-size: 1.4rem;
  font-weight: 500;
  color: #4a5568;
  margin: 0;
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 4rem 0;
`;

const ChangesList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ChangeItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #f7fafc;
  border-radius: 0.8rem;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const ChangeItem = styled.li<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1rem 1.2rem;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #edf2f7;
  }

  ${({ isExpanded }) => isExpanded && `background: #edf2f7;`}
`;

const ChangeOrdinal = styled.span`
  flex-shrink: 0;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  background: black;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Inter, serif;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ChangeLabel = styled.span`
  font-family: Inter, serif;
  font-size: 1.2rem;
  font-weight: 400;
  color: #4a5568;
  flex: 1;
`;

const ExpandIconWrapper = styled.div`
  display: flex;
  align-items: center;
  color: #a0aec0;
`;

const ChangeDescription = styled.div`
  padding: 1rem 1.2rem 1rem 4.8rem;
  font-family: Inter, serif;
  font-size: 1.1rem;
  font-weight: 400;
  color: #718096;
  line-height: 1.5;
`;

const EmptyState = styled.p`
  font-family: Inter, serif;
  font-size: 1.2rem;
  color: #718096;
  text-align: center;
  margin: 0;
`;

const Footer = styled.div`
  display: flex;
  gap: 1.2rem;
  padding: 1.5rem 2rem;
  justify-content: center;
`;

const PublishButton = styled.button`
  cursor: pointer;
  appearance: none;
  outline: none;
  background: black;
  border: 1.5px solid black;
  color: white;
  border-radius: 1rem;
  padding: 1rem 3rem;
  font-size: 1.4rem;
  font-weight: 600;
  font-family: Inter, serif;

  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  :hover {
    background: #eaeaea;
    color: black;
    border-color: black;
  }

  :active {
    opacity: 0.6;
  }
`;

const IgnoreButton = styled(PublishButton)`
  && {
    border: 1.5px solid black;
    background: transparent;
    color: black;

    :hover {
      background: #eaeaea;
      color: black;
      border-color: black;
    }
  }
`;
